import React, { useState, useEffect } from 'react';
import { FileText, Loader2, Plus, RefreshCw, Upload } from 'lucide-react';
import { Button } from '@/src/shadComponents/ui/button';
import { Badge } from '@/src/shadComponents/ui/badge';
import Table, { createColumnHelper } from '../../Common/components/Table';
import useUserOrg from '../../Authentication/components/hooks/useUserOrg';
import {
    useDeviceRegistrationAutoenroll,
    DeviceRegistrationAutoenroll,
    CreateDeviceRegistrationRequest,
} from './hooks/useDeviceRegistrationAutoenroll';
import CsvImportModal from './CsvImportModal';
import DeviceRegistrationCreateModal from './DeviceRegistrationCreateModal';
import DeviceRegistrationActions from './DeviceRegistrationActions';
import { toast } from 'sonner';
import moment from 'moment-timezone';
import useRerender from '../../Common/components/hooks/useRerender';

const columnHelper = createColumnHelper<DeviceRegistrationAutoenroll>();

export default function AutoEnrollment() {
    const { orgId } = useUserOrg();
    const {
        listDeviceRegistrations,
        createDeviceRegistrations,
        createSingleDeviceRegistration,
        isLoading,
    } = useDeviceRegistrationAutoenroll(orgId);
    const [deviceRegistrations, setDeviceRegistrations] = useState<
        DeviceRegistrationAutoenroll[]
    >([]);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const { render, renderCount } = useRerender();

    const loadDeviceRegistrations = async () => {
        setIsRefreshing(true);
        try {
            const registrations = await listDeviceRegistrations();
            setDeviceRegistrations(registrations);
        } catch (error) {
            toast.error('Failed to load device registrations');
            console.error('Error loading device registrations:', error);
        } finally {
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        loadDeviceRegistrations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, renderCount]);

    const handleImport = async (devices: CreateDeviceRegistrationRequest[]) => {
        try {
            await createDeviceRegistrations(devices);
            toast.success(
                `Successfully imported ${devices.length} device${
                    devices.length !== 1 ? 's' : ''
                }`,
            );
            await loadDeviceRegistrations(); // Refresh the list
        } catch (error) {
            toast.error('Failed to import devices');
            throw error; // Re-throw to let modal handle the error
        }
    };

    const onDelete = () => {
        toast.success('Device registration deleted');
        render(); // Refresh the list
    };

    const handleCreateSingle = async (
        device: CreateDeviceRegistrationRequest,
    ) => {
        try {
            await createSingleDeviceRegistration(device);
            toast.success(
                `Successfully created device registration for "${device.deviceName}"`,
            );
        } catch (error) {
            toast.error('Failed to create device registration');
            throw error; // Re-throw to let modal handle the error
        }
    };

    const onDeviceCreated = () => {
        render(); // Refresh the list
    };

    const columnDefinitions = [
        columnHelper.accessor('deviceId', {
            id: 'deviceId',
            header: () => <span>Serial Number</span>,
            cell: (info) => (
                <span className="font-mono text-sm">{info.getValue()}</span>
            ),
        }),
        columnHelper.accessor('deviceName', {
            id: 'deviceName',
            header: () => <span>Device Name</span>,
            cell: (info) => (
                <span className="font-medium">{info.getValue()}</span>
            ),
        }),
        columnHelper.accessor('groupNames', {
            id: 'groupNames',
            header: () => <span>Groups</span>,
            cell: (info) => {
                const groups = info.getValue();
                if (!groups || groups.trim() === '') {
                    return <span className="text-muted-foreground italic">None</span>;
                }
                const groupList = groups
                    .split(',')
                    .map((g) => g.trim())
                    .filter((g) => g !== '');
                return (
                    <div className="flex flex-wrap gap-1">
                        {groupList.map((group, index) => (
                            <Badge
                                key={index}
                                variant="secondary"
                                className="text-xs"
                            >
                                {group}
                            </Badge>
                        ))}
                    </div>
                );
            },
        }),
        columnHelper.accessor('counter', {
            id: 'counter',
            header: () => <span>Usage Count</span>,
            cell: (info) => (
                <span className="text-sm">
                    {info.getValue()} time{info.getValue() !== 1 ? 's' : ''}
                </span>
            ),
        }),
        columnHelper.accessor('createdAt', {
            id: 'createdAt',
            header: () => <span>Created</span>,
            cell: (info) => (
                <span className="text-sm text-muted-foreground">
                    {moment(info.getValue()).format('MMM D, YYYY')}
                </span>
            ),
        }),
        columnHelper.display({
            id: 'actions',
            header: () => <span>Actions</span>,
            cell: (props: { row: any }) => (
                <DeviceRegistrationActions
                    orgId={orgId}
                    row={props.row}
                    onDelete={onDelete}
                />
            ),
        }),
    ];

    if (isLoading && deviceRegistrations.length === 0) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div>
            <div className="flex mx-5 mb-6">
                <div className="flex-1">
                    <h2 className="text-lg font-semibold tracking-tight text-foreground">
                        Auto Enrollment
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Manage device pre-registration for automatic enrollment.
                        Devices can use their registered serial number to
                        automatically obtain credentials and be assigned to
                        groups.
                        <br />
                        <br />
                        <strong>
                            For all iOS apps, the device must be managed and
                            configured with the `DeviceSerialNumber` key set in
                            the `com.apple.configuration.managed` profile.
                        </strong>
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        onClick={loadDeviceRegistrations}
                        disabled={isRefreshing}
                    >
                        <RefreshCw
                            className={`size-4 ${
                                isRefreshing ? 'animate-spin' : ''
                            }`}
                        />
                        Refresh
                    </Button>
                    <Button onClick={() => setIsCreateModalOpen(true)}>
                        <Plus className="size-4" />
                        Add Device
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => setIsImportModalOpen(true)}
                    >
                        <Upload className="size-4" />
                        Import CSV
                    </Button>
                </div>
            </div>

            {deviceRegistrations.length === 0 ? (
                <div className="text-center py-12">
                    <div className="mx-auto max-w-md">
                        <FileText className="mx-auto size-12 text-muted-foreground" />
                        <h3 className="mt-2 text-sm font-medium text-foreground">
                            No devices registered
                        </h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                            Get started by adding a single device or importing
                            multiple devices from a CSV file.
                        </p>
                    </div>
                </div>
            ) : (
                <Table
                    data={deviceRegistrations}
                    columns={columnDefinitions}
                />
            )}

            <DeviceRegistrationCreateModal
                open={isCreateModalOpen}
                onOpenChange={setIsCreateModalOpen}
                onDeviceCreated={onDeviceCreated}
                onCreateDevice={handleCreateSingle}
            />

            <CsvImportModal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                onImport={handleImport}
                existingDevices={deviceRegistrations}
            />
        </div>
    );
}
